import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, mensajeError } from "../api/client";
import { PageHeader } from "../components/ui/PageHeader";
import { Spinner } from "../components/ui/Spinner";

function haceDias(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  // OJO: nunca usar toISOString().slice(0,10) aquí — convierte a UTC y puede
  // regresar el día equivocado según la hora y el huso horario del navegador.
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// El backend compara instantes absolutos, no fechas "naive". Un input
// type="date" solo da "YYYY-MM-DD"; hay que anclarlo al inicio/fin de ESE día
// calendario en la zona horaria del navegador (no UTC del servidor) antes de
// mandarlo, o las ventas de hoy quedan fuera del rango (mismo bug que ya se
// corrigió en la Agenda).
function inicioDiaISO(fechaLocal: string) {
  return new Date(`${fechaLocal}T00:00:00`).toISOString();
}
function finDiaISO(fechaLocal: string) {
  return new Date(`${fechaLocal}T23:59:59.999`).toISOString();
}

export default function Reportes() {
  const [desde, setDesde] = useState(haceDias(30));
  const [hasta, setHasta] = useState(haceDias(0));
  const [datos, setDatos] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get("/reportes/ventas", { params: { desde: inicioDiaISO(desde), hasta: finDiaISO(hasta) } })
      .then((res) => setDatos(res.data))
      .catch((err) => setError(mensajeError(err)));
  }, [desde, hasta]);

  return (
    <div>
      <PageHeader titulo="Reportes y estadísticas" descripcion="Ventas, citas y productos y servicios para tomar mejores decisiones." icono={<BarChart3 size={20} />} />

      <div className="card mb-6 flex flex-wrap items-end gap-4 p-4">
        <div>
          <label className="label">Desde</label>
          <input type="date" className="input" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div>
          <label className="label">Hasta</label>
          <input type="date" className="input" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {!datos ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="card p-5">
              <p className="text-xs text-slate-400">Ventas totales del periodo</p>
              <p className="text-2xl font-semibold text-slate-800">${datos.total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="card p-5">
              <p className="text-xs text-slate-400">Número de ventas</p>
              <p className="text-2xl font-semibold text-slate-800">{datos.numeroVentas}</p>
            </div>
          </div>

          <div className="card p-5">
            <h3 className="mb-4 text-sm font-semibold text-slate-700">Ventas por día</h3>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={datos.porDia}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => `$${v.toFixed(2)}`} />
                  <Line type="monotone" dataKey="total" stroke="#0EA5A4" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
